/**
 * VoiceUpgradeModal — modal de upgrade pra tier free/plantonista
 * que clicou no botão VOZ (web /ia, Fase Voice S2).
 *
 * Espelha apps/extension/src/sidebar/components/ai/VoiceUpgradeModal.tsx,
 * adaptado para web:
 *   - Sem useCaktoCheckout (não existe no web)
 *   - window.open direto pros 2 URLs Cakto BRL (Especialista R$ 175,
 *     Unlimited R$ 399), em nova aba com noopener/noreferrer
 *   - Strings via prop `t` (5 idiomas)
 *
 * onUpgrade recebe a key 'especialistaMensal' | 'unlimitedMensal' — pai
 * (App.tsx) decide o que fazer; o handler default simplesmente abre tab.
 */
import type { FC } from 'react';

/** Keys das URLs Cakto suportadas (mesmas labels da extensão). */
export type VoiceUpgradePlan = 'especialistaMensal' | 'unlimitedMensal';

/** URLs Cakto BRL — espelham as do PaywallCinematic + extension. */
export const CAKTO_URLS: Record<VoiceUpgradePlan, string> = {
  especialistaMensal: 'https://pay.cakto.com.br/6y6b3f8_868238',
  unlimitedMensal: 'https://pay.cakto.com.br/9c27rk2_868247',
};

export interface VoiceUpgradeModalProps {
  /** Tier atual (define mensagem contextual). */
  readonly tier: 'free' | 'plantonista';
  /** Fecha modal sem ação. */
  readonly onClose: () => void;
  /** Dispara checkout Cakto na key escolhida. */
  readonly onUpgrade: (plan: VoiceUpgradePlan) => void;
  /** Função de tradução do useTranslations. */
  readonly t: (key: string, vars?: Record<string, string | number>) => string;
}

/* ------------------------------------------------------------------------- */
/* Helper puro                                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Escolhe chaves i18n contextuais por tier:
 *   - plantonista: já paga → "no teu plano atual ainda não tem voz"
 *   - free:        primeira apresentação → "grave no celular entre pacientes"
 */
export function getUpgradeMessageKeys(tier: 'free' | 'plantonista'): {
  primaryKey: string;
  secondaryKey: string;
} {
  if (tier === 'plantonista') {
    return {
      primaryKey: 'voice.upgrade.body',
      secondaryKey: 'voice.upgrade.subbody_plantonista',
    };
  }
  return {
    primaryKey: 'voice.upgrade.body',
    secondaryKey: 'voice.upgrade.subbody_free',
  };
}

/* ------------------------------------------------------------------------- */
/* Componente                                                                  */
/* ------------------------------------------------------------------------- */

const VoiceUpgradeModal: FC<VoiceUpgradeModalProps> = ({ tier, onClose, onUpgrade, t }) => {
  const keys = getUpgradeMessageKeys(tier);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-upgrade-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        background: 'rgba(34,37,50,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--ink-soft)',
          border: '1px solid var(--violet)',
          boxShadow: '0 0 24px rgba(124,124,248,0.18)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header — título + close */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '12px 12px 10px',
            background: 'var(--ink)',
            borderBottom: '1px solid rgba(124,124,248,0.30)',
            flexShrink: 0,
          }}
        >
          <h2
            id="voice-upgrade-title"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.04em',
              margin: 0,
              whiteSpace: 'pre',
              lineHeight: 1.4,
            }}
          >
            {t('voice.upgrade.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: 'transparent',
              border: '1px solid transparent',
              color: 'var(--ink-mute)',
              fontFamily: 'var(--font-sans)',
              fontSize: 18,
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
              flexShrink: 0,
              marginLeft: 8,
              transition: 'color 150ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--ink-mute)')}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: 600,
              color: '#FFFFFF',
              textShadow: 'none',
              lineHeight: 1.5,
            }}
          >
            {t(keys.primaryKey)}
          </p>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              color: 'var(--ink-light)',
              lineHeight: 1.55,
            }}
          >
            {t(keys.secondaryKey)}
          </p>

          {/* CTA 1 — Especialista (primary blue) */}
          <button
            type="button"
            onClick={() => onUpgrade('especialistaMensal')}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: 'var(--cta-blue)',
              color: '#FFFFFF',
              border: '1px solid var(--cta-blue)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              borderRadius: 0,
              boxShadow: 'none',
              marginTop: 4,
              transition: 'box-shadow 180ms ease, border-color 180ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.borderColor = '#FF6A00';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'var(--cta-blue)';
            }}
          >
            {t('voice.upgrade.cta_especialista')}
          </button>

          {/* CTA 2 — Unlimited (accent violet) */}
          <button
            type="button"
            onClick={() => onUpgrade('unlimitedMensal')}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: 'rgba(124,124,248,0.14)',
              color: '#FFFFFF',
              border: '1px solid var(--violet)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              borderRadius: 0,
              boxShadow: 'none',
              transition: 'box-shadow 180ms ease, border-color 180ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              e.currentTarget.style.borderColor = '#FF6A00';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'var(--violet)';
            }}
          >
            {t('voice.upgrade.cta_unlimited')}
          </button>

          {/* Disclaimer login */}
          <p
            style={{
              margin: '8px 0 0',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              color: 'var(--ink-mute)',
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            {t('voice.upgrade.already_subscriber')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceUpgradeModal;
